import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Gauge, SlidersHorizontal, MonitorSmartphone, Wrench, Shield, Zap } from "lucide-react";
import { MaskedLines, FadeUp, Eyebrow, EASE, SectionHead } from "@/components/Reveal";
import DemoForm from "@/components/DemoForm";
import ProductSchema from "@/components/ProductSchema";

const HERO_IMG =
  "https://images.pexels.com/photos/13923406/pexels-photo-13923406.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const CHIPS = ["±20 mm blade accuracy", "Automatic blade control", '10.1" tablet', "30 min calibration"];

const FEATURES = [
  { icon: Gauge, title: "DO-Tech precision", body: "Dual GNSS, a 100 Hz IMU and the DO-Tech algorithm hold the blade to ±20 mm — fine-grading accuracy while you push." },
  { icon: SlidersHorizontal, title: "Automatic blade control", body: "Real-time hydraulic adjustment at varying speeds and in any blade position. Finished grade, automated." },
  { icon: MonitorSmartphone, title: '10.1" tablet', body: "A big, bright operator interface with a deliberately simple calibration process — no surveyor needed." },
  { icon: Wrench, title: "Pilot & PVG compatible", body: "Runs with Pilot and PVG dozer valve systems, plus multiple coordinate and design file formats." },
  { icon: Shield, title: "Built for harsh sites", body: "Rugged, durable components designed to shrug off dust, rain and the worst ground conditions." },
  { icon: Zap, title: "30-minute calibration", body: "A single IMU sensor and a quick calibration — the dozer is fine-grading the same day we install." },
];

const USE_CASES = ["Land clearing", "Foundation preparation", "Dam & levee construction", "Embankment construction"];

const SPECS = [
  { k: "Blade accuracy", v: "±20 mm — dual GNSS + 100 Hz IMU + DO-Tech (manufacturer rating)" },
  { k: "Max precision", v: "10 mm H / 15 mm V" },
  { k: "Positioning", v: "Full GNSS · dual antennas" },
  { k: "Tilt sensor", v: "100 Hz IMU" },
  { k: "Display", v: '10.1" tablet' },
  { k: "Connectivity", v: "Wi-Fi · Bluetooth · 4G LTE · 410–470 MHz Rx · CAN · RS232" },
  { k: "Valve integration", v: "Pilot & PVG dozer valve modules" },
  { k: "Calibration", v: "~30 min · single IMU sensor" },
];

const PRODUCT_SCHEMA = {
  "@type": "Product",
  name: "CHCNAV TD63 Pro — 3D Dozer Machine Control",
  image: HERO_IMG,
  description:
    "3D dozer machine control with ±20 mm automatic blade control, 10.1-inch tablet and 30-minute calibration. Supplied, installed and supported across Australia by Green Grade.",
  sku: "TD63-Pro",
  model: "TD63 Pro",
  category: "Construction machine control",
  brand: { "@type": "Brand", name: "CHCNAV" },
  manufacturer: { "@type": "Organization", name: "CHCNAV" },
  url: "https://greengrade.au/products/dozer",
};

export default function ProductDozer() {
  return (
    <main>
      <ProductSchema product={PRODUCT_SCHEMA} />
      <section className="relative flex min-h-[88svh] items-end overflow-hidden" data-testid="product-dozer-hero">
        <motion.div
          initial={{ scale: 1.12 }}
          animate={{ scale: 1 }}
          transition={{ duration: 1.6, ease: EASE }}
          className="absolute inset-0"
        >
          <img src={HERO_IMG} alt="Dozer pushing dirt on a construction site" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-ink/75" />
          <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/30 to-ink/70" />
        </motion.div>
        <div className="absolute inset-0 bg-blueprint" />
        <div className="relative mx-auto w-full max-w-[1600px] px-4 pb-20 pt-32 sm:px-8 lg:px-16">
          <FadeUp y={12}>
            <Eyebrow>
              <span>
                <Link to="/precision-solutions" data-testid="dozer-eyebrow-chcnav-link" className="underline decoration-neon/40 underline-offset-4 transition-colors hover:text-white">
                  CHCNAV
                </Link>{" "}
                TD63 Pro
              </span>
            </Eyebrow>
          </FadeUp>
          <MaskedLines
            as="h1"
            delay={0.15}
            lines={["Automatic dozer", { text: "control.", lineClass: "italic text-neon" }]}
            className="mt-4 font-display text-6xl font-black uppercase leading-[0.9] tracking-tight text-white sm:text-8xl lg:text-9xl"
          />
          <p className="mt-8 max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg">
            Automatic 3D blade control for dozers — dual GNSS, a 100 Hz IMU and the DO-Tech algorithm holding ±20 mm
            while you push. Fine grade without a single stake.
          </p>
          <div className="mt-8 flex flex-wrap gap-2">
            {CHIPS.map((c) => (
              <span key={c} className="border border-signal/40 bg-ink/60 px-4 py-2 font-mono text-[11px] uppercase tracking-[0.2em] text-neon backdrop-blur-sm">
                {c}
              </span>
            ))}
          </div>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link
              to="/contact"
              data-testid="product-dozer-demo-cta"
              className="group inline-flex items-center gap-3 bg-brand px-8 py-4 font-display text-lg font-bold uppercase tracking-wide text-cream transition-colors hover:bg-signal hover:text-deep"
            >
              Request a demo
              <ArrowUpRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </Link>
            <Link
              to="/#quote"
              data-testid="product-dozer-quote-cta"
              className="inline-flex items-center gap-3 border border-signal/50 px-8 py-4 font-display text-lg font-bold uppercase tracking-wide text-neon transition-colors hover:bg-signal/10"
            >
              Build a quote
            </Link>
            <Link
              to="/products/rover"
              data-testid="product-dozer-to-rover-link"
              className="inline-flex items-center gap-3 border border-line px-8 py-4 font-display text-lg font-bold uppercase tracking-wide text-white transition-colors hover:border-signal hover:text-neon"
            >
              Pair it with the rover
            </Link>
          </div>
        </div>
      </section>

      <section className="px-4 py-20 sm:px-8 lg:px-16 lg:py-32" data-testid="product-dozer-features">
        <div className="mx-auto max-w-[1600px]">
          <SectionHead kicker="What it does" title="The blade follows the model" body="Push, spread and fine-grade with the design surface driving the blade — automatically, in real time." />
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {FEATURES.map((f, i) => (
              <FadeUp key={f.title} delay={i * 0.06}>
                <div className="h-full border border-line bg-surface p-6 transition-colors duration-300 hover:border-signal/50 sm:p-8">
                  <f.icon className="h-6 w-6 text-signal" />
                  <h3 className="mt-5 font-display text-xl font-bold uppercase tracking-tight text-white">{f.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-fog">{f.body}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      <section className="border-y border-line bg-deep px-4 py-20 sm:px-8 lg:px-16 lg:py-28" data-testid="product-dozer-usecases">
        <div className="mx-auto max-w-[1600px]">
          <SectionHead kicker="Where it earns" title="Made for mass earthworks" />
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {USE_CASES.map((u, i) => (
              <FadeUp key={u} delay={i * 0.08}>
                <div className="border border-line bg-surface p-8 transition-colors hover:border-signal/50">
                  <span className="text-stroke font-display text-5xl font-black">{String(i + 1).padStart(2, "0")}</span>
                  <h3 className="mt-4 font-display text-xl font-bold uppercase text-white">{u}</h3>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-line bg-deep px-4 py-20 sm:px-8 lg:px-16 lg:py-32" data-testid="product-dozer-specs">
        <div className="mx-auto max-w-[1600px]">
          <SectionHead kicker="Technical" title="Specifications" />
          <div className="mt-12">
            {SPECS.map((s, i) => (
              <FadeUp key={s.k} delay={i * 0.04} y={16}>
                <div className="flex flex-col gap-1 border-b border-line py-5 sm:flex-row sm:items-baseline sm:justify-between">
                  <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-fog">{s.k}</span>
                  <span className="max-w-[60%] text-sm text-white sm:text-right sm:text-base">{s.v}</span>
                </div>
              </FadeUp>
            ))}
          </div>
          <FadeUp delay={0.1} className="mt-10">
            <Link
              to="/specifications#td63"
              data-testid="product-dozer-spec-link"
              className="inline-flex items-center gap-2 border border-line px-6 py-3 font-mono text-[11px] uppercase tracking-[0.25em] text-fog transition-colors hover:border-signal hover:text-neon"
            >
              Full specification breakdown <ArrowUpRight className="h-4 w-4" />
            </Link>
          </FadeUp>
        </div>
      </section>

      <section className="px-4 py-20 sm:px-8 lg:px-16 lg:py-32" data-testid="product-dozer-form">
        <div className="mx-auto max-w-[1600px]">
          <SectionHead kicker="Get it on your machine" title="Request a TD63 Pro demo" body="See automatic blade control push grade on your dozer — installed and calibrated on your site." />
          <FadeUp delay={0.12} className="mt-12">
            <DemoForm />
          </FadeUp>
        </div>
      </section>
    </main>
  );
}
